import { useState, useCallback, memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';

const CallToActionCard = memo(
	({
		card,
		onUpdate,
		isLocked,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
	}) => {
		const [editingField, setEditingField] = useState(null);
		const [editValues, setEditValues] = useState({
			title: card.data?.title || '',
			subtitle: card.data?.subtitle || '',
			buttonText: card.data?.buttonText || '',
		});

		useEffect(() => {
			setEditValues({
				title: card.data?.title || '',
				subtitle: card.data?.subtitle || '',
				buttonText: card.data?.buttonText || '',
			});
		}, [card.data?.title, card.data?.subtitle, card.data?.buttonText]);

		const handleStartEdit = useCallback(
			(field, e) => {
				e?.stopPropagation();
				if (isLocked || !isEditMode) return;
				setEditingField(field);
			},
			[isLocked, isEditMode]
		);

		const handleFinishEdit = useCallback(() => {
			setEditingField(null);
			if (onUpdate && editValues[editingField]) {
				onUpdate({
					...card.data,
					[editingField]: editValues[editingField],
				});
			}
		}, [editingField, editValues, card.data, onUpdate]);

		const handleChange = useCallback((field, value) => {
			setEditValues((prev) => ({ ...prev, [field]: value }));
		}, []);

		const handleKeyDown = useCallback(
			(e) => {
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					handleFinishEdit();
				} else if (e.key === 'Escape') {
					setEditingField(null);
					setEditValues({
						title: card.data?.title || '',
						subtitle: card.data?.subtitle || '',
						buttonText: card.data?.buttonText || '',
					});
				}
			},
			[handleFinishEdit, card.data]
		);

		const cardData = card.data || {};
		const backgroundColor = cardData.backgroundColor || '#3b82f6';
		const borderColor = cardData.borderColor || '#2563eb';
		const textColor = cardData.textColor || '#ffffff';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 2;
		const buttonBackground = cardData.buttonBackground || '#ffffff';
		const buttonTextColor = cardData.buttonTextColor || backgroundColor;
		const link = cardData.link || '';
		const isExternalLink =
			link.startsWith('http://') || link.startsWith('https://');

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#2563eb', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		const renderEditableText = (
			field,
			className,
			placeholder,
			customStyles = {}
		) => {
			const isEditing = editingField === field;
			const displayValue = cardData[field] || placeholder;
			const editValue = editValues[field] || displayValue;

			const mergedStyles = {
				color: textColor,
				...customStyles,
				maxWidth: '100%',
			};

			if (isEditing) {
				const textLength = editValue.length || placeholder.length;
				const dynamicWidth = `${Math.min(Math.max(textLength * 13, 13), 95)}%`;

				return (
					<input
						type="text"
						value={editValue}
						onChange={(e) => handleChange(field, e.target.value)}
						onBlur={handleFinishEdit}
						onKeyDown={handleKeyDown}
						className={`${className} bg-transparent border-2 border-white border-dashed rounded px-2 py-1 focus:outline-none focus:border-opacity-100`}
						style={{
							...mergedStyles,
							width: dynamicWidth,
							maxWidth: '95%',
							minWidth: '100px',
						}}
						autoFocus
						onClick={(e) => e.stopPropagation()}
						data-no-drag
					/>
				);
			}

			return (
				<div
					className={`${className} ${!isLocked && isEditMode ? 'cursor-text hover:opacity-80 transition-opacity' : ''}`}
					onClick={(e) => {
						if (isEditMode) {
							e.stopPropagation();
							handleStartEdit(field, e);
						}
					}}
					onPointerDown={(e) => {
						if (isEditMode) e.stopPropagation();
					}}
					style={mergedStyles}
					data-no-drag={isEditMode ? true : undefined}
				>
					{displayValue}
				</div>
			);
		};

		const titleStyles = {
			fontSize: 'clamp(1.1rem, 1.75rem + 2vw, 60px)',
			textTransform: 'uppercase',
			fontFamily: 'Nunito Sans, sans-serif',
			fontWeight: 800,
			lineHeight: 0.9,
			marginBottom: '0',
			wordBreak: 'break-word',
		};

		const subtitleStyles = {
			fontSize: 'clamp(1rem, .75vw + .5rem, 22px)',
			textTransform: 'uppercase',
			fontFamily: 'Nunito Sans, sans-serif',
			fontWeight: 600,
			lineHeight: 1.1,
			marginBottom: '2px',
			wordBreak: 'break-word',
		};

		const buttonStyles = {
			fontSize: 'clamp(.75rem, 0.75rem + 0.5vw, 16px)',
			textTransform: 'uppercase',
			fontFamily: '"Nunito Sans", sans-serif',
			fontWeight: 700,
			lineHeight: 1,
			borderRadius: '20px',
			padding: '12px 20px',
			textAlign: 'center',
		};

		return (
			<div
				className="w-full h-full flex flex-col items-center justify-center relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					gap: '8px',
					padding: '16px',
				}}
			>
				{renderEditableText(
					'title',
					'text-2xl font-bold text-center',
					'Title',
					titleStyles
				)}
				{renderEditableText(
					'subtitle',
					'text-base text-center opacity-90',
					'Subtitle',
					subtitleStyles
				)}

				{editingField === 'buttonText' ? (
					<div
						className="inline-block"
						style={{
							backgroundColor: buttonBackground,
							color: buttonTextColor,
							...buttonStyles,
							marginTop: '-12px',
							marginBottom: '-12px',
						}}
						data-no-drag
					>
						<input
							type="text"
							value={editValues.buttonText}
							onChange={(e) => handleChange('buttonText', e.target.value)}
							onBlur={handleFinishEdit}
							onKeyDown={handleKeyDown}
							className="bg-transparent border-2 border-blue-300 border-dashed rounded px-2 py-1 focus:outline-none text-center"
							style={{
								color: buttonTextColor,
								width: `${Math.max((editValues.buttonText || 'Button').length * 0.7, 6)}em`,
								maxWidth: '90%',
								minWidth: '100px',
							}}
							autoFocus
							onClick={(e) => {
								e.stopPropagation();
							}}
							onPointerDown={(e) => {
								e.stopPropagation();
							}}
							onMouseDown={(e) => {
								e.stopPropagation();
							}}
							data-no-drag
						/>
					</div>
				) : isExternalLink && !isEditMode ? (
					<a
						href={link}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-block hover:scale-105 hover:shadow-lg"
						style={{
							backgroundColor: buttonBackground,
							color: buttonTextColor,
							...buttonStyles,
							transition: 'all 0.3s ease-in-out',
						}}
					>
						<div>{cardData.buttonText || 'Button'}</div>
					</a>
				) : (
					<Link
						to={isEditMode ? '#' : link}
						className="inline-block hover:scale-105 hover:shadow-lg"
						style={{
							backgroundColor: buttonBackground,
							color: buttonTextColor,
							...buttonStyles,
							transition: 'all 0.3s ease-in-out',
						}}
						onClick={(e) => {
							if (isEditMode) {
								e.preventDefault();
								e.stopPropagation();
								handleStartEdit('buttonText', e);
								return false;
							}
							const scrollContainer = document.querySelector('.container-fluid');
							if (scrollContainer) scrollContainer.scrollTop = 0;
						}}
						data-no-drag={isEditMode ? true : undefined}
					>
						<div
							className={
								!isLocked && isEditMode
									? 'cursor-text hover:opacity-80 transition-opacity'
									: ''
							}
						>
							{cardData.buttonText || 'Button'}
						</div>
					</Link>
				)}
			</div>
		);
	}
);

CallToActionCard.displayName = 'CallToActionCard';

export default CallToActionCard;
